import { Button } from "@admin/components/atoms/Button";
import { Label } from "@admin/components/atoms/Input/Label";
import { useCurrentUserGroups } from "@admin/hooks/useCurrentUserGroups";
import { FermentedVegetableModal } from "@admin/pages/food-management/control-points/components/Modals/FermentedVegetableModal";
import {
  buttonColorMap,
  FoodManagementControls,
  InstanceStatusColorAndMeanings,
  isWithinLast24Hours,
  StatusBgColors,
} from "@enos-monorepo/lib";
import { FoodControlPointInstance } from "@enos-monorepo/lib";
import { FermentedVegetableStart } from "@enos-monorepo/lib";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import { CorrectiveActions } from "../CorrectiveActions/CorrectiveActions";
import { DeleteInstanceModal } from "../DeleteInstanceModal";

interface FermentedVefetablesControlTextsProps {
  datas: {
    controlData: FermentedVegetableStart;
    instance: FoodControlPointInstance;
  }[];
  type?: FoodManagementControls;
  callBack?: () => void;
}

export default function FermentedVegetablesControlTexts({
  datas,
  type,
  callBack,
}: FermentedVefetablesControlTextsProps) {
  const fermentedBegin = type === FoodManagementControls.vegetablespHbeginning;
  const [selectedInstance, setSelectedInstance] =
    useState<FoodControlPointInstance>();
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);

  const { t } = useTranslation();

  const onDelete = useCallback((instance: FoodControlPointInstance) => {
    setSelectedInstance(instance);
    setIsDeleteOpen(true);
  }, []);

  const onEdit = useCallback((instance: FoodControlPointInstance) => {
    setSelectedInstance(instance);
    setIsEditOpen(true);
  }, []);

  const { isSuperAdmin } = useCurrentUserGroups();

  return (
    <div className="flex flex-col gap-5">
      {datas?.map((data, index) => {
        const status = InstanceStatusColorAndMeanings(t).find(
          (i) => i.type === data.instance.status,
        );

        const bgColorClass =
          StatusBgColors[
            data?.controlData?.status as keyof typeof StatusBgColors
          ];

        const buttonColor = buttonColorMap[data?.controlData?.status];

        return (
          <div
            className={`${bgColorClass} rounded-xl`}
            key={data.controlData.controlPointInstanceId ?? "" + index}
          >
            {(isSuperAdmin ||
              isWithinLast24Hours(data.instance.updatedDate)) && (
              <div className="flex flex-row justify-end">
                <Button
                  size="sm"
                  property={buttonColor}
                  onClick={() => onEdit(data.instance)}
                >
                  Edit
                </Button>
              </div>
            )}
            <div
              className="px-5 py-8"
              key={(data.controlData.controlPointInstanceId ?? "") + index}
            >
              <Label title={t("common.Status")}>
                <span style={{ color: status?.color }}> {status?.name} </span>
              </Label>

              <Label title={"ph"}>{data.controlData.ph}</Label>

              {fermentedBegin && (
                <Label title={t("food.Salt Content")}>
                  {data.controlData.saltContent}
                </Label>
              )}

              {fermentedBegin && (
                <Label title={t("food.Storage Temperature")}>
                  {data.controlData.storageTemperature}
                </Label>
              )}

              {data.instance.remark && (
                <Label title={t("food.Remarks")}>{data.instance.remark}</Label>
              )}

              {data.instance.reason && !fermentedBegin && (
                <Label title={t("food.Corrective Action")}>
                  <CorrectiveActions correctiveActions={data.instance.reason} />
                </Label>
              )}
            </div>
            {isSuperAdmin && (
              <div className="flex flex-row justify-end">
                <Button
                  size="sm"
                  property="danger"
                  onClick={() => onDelete(data.instance)}
                >
                  {t("common.Delete")}
                </Button>
              </div>
            )}
          </div>
        );
      })}

      {selectedInstance && !isDeleteOpen && (
        <FermentedVegetableModal
          open={isEditOpen}
          type={
            type as
              | FoodManagementControls.vegetablespHbeginning
              | FoodManagementControls.vegetablespH48Hours
              | FoodManagementControls.vegetablespHfinal
          }
          onClose={() => setIsEditOpen(false)}
          callBack={callBack}
          foodControlPointInstance={selectedInstance}
          initialData={JSON.parse(selectedInstance?.data || "{}")}
        />
      )}

      {selectedInstance && (
        <DeleteInstanceModal
          open={isDeleteOpen}
          onClose={() => setIsDeleteOpen(false)}
          selectedInstance={selectedInstance}
          callBack={callBack}
        />
      )}
    </div>
  );
}
